import type { Site } from "../../../../apiClient/generated";
import {
    AlignedDataDisplay,
    CoordinatesField,
} from "../../../../ui/GenericFields";
import { formatDate } from "../../../../utils/dates";

interface SiteInfoProps {
    site: Site | undefined;
}

export const SiteInfo = ({ site }: SiteInfoProps) => {
    if (!site) return null;

    const data: { label: string; value: React.ReactNode }[] = [
        { label: "Site Name", value: site.siteName },
        { label: "Operator Unique ID", value: site.operatorUniqueId },
        site.responsiblePerson
            ? {
                  label: "Responsible",
                  value: site.responsiblePersonName,
              }
            : undefined,
        {
            label: "Location",
            value: (
                <CoordinatesField
                    coordinates={site.location?.coordinates}
                    allowCopy
                />
            ),
        },
        site.siteTypeFromImagery
            ? {
                  label: "Site Type (from imagery)",
                  value: (
                      <span className="capitalize">
                          {site.siteTypeFromImagery.replaceAll("_", " ")}
                      </span>
                  ),
              }
            : undefined,
        { label: "Subpart W Segment", value: site.subpartWSegment },
        { label: "Other Operator IDs", value: site.otherOperatorIds },
        { label: "Status", value: site.operatorStatus },
        { label: "Data Source", value: site.siteDataSource },
        {
            label: "Start Date",
            value: site.startDate && formatDate(site.startDate),
        },
        {
            label: "End Date",
            value: site.endDate && formatDate(site.endDate),
        },
        ...Object.entries(site.extraData || {})
            .toSorted((a, b) => a[0].localeCompare(b[0]))
            .flatMap(([key, value]) => {
                let cleanValue: string | undefined;
                if (typeof value === "string") {
                    cleanValue = value;
                } else if (typeof value === "number") {
                    cleanValue = value.toString();
                } else if (typeof value === "object") {
                    cleanValue = JSON.stringify(value);
                }
                return cleanValue ? [{ label: key, value: cleanValue }] : [];
            }),
    ].filter(Boolean);

    return (
        <div className="overflow-auto p-2">
            <AlignedDataDisplay data={data} />
        </div>
    );
};
